import { ColumnDef } from '@tanstack/react-table';
import queryString from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, FrontAppList } from '../../../../@types';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Divider, Text, useColorMode } from 'theme-ui';
import FrontAppTable from '.';
import { UpdateFrontAppRow, getAllFrontapp } from '../../../store/actions/frontApp.Action';
import { getApiEndPoint } from '../../../utils/GenerateQueryString';
import { baseUrl } from '../../../utils/global';
import { TabelHeadingTxt } from '../../Text/TabelCommonTxt';
import Export from "../../assets/images/Icon/Export.svg";
import calender from "../../assets/images/calendar-2.svg";
import CheckboxInput from './Checkbox';
import ProjectNameSelect, { ManagerSelect } from './ProjectNameSelect';
import CommentBox from './CommentBox';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { updateBulkDTPM } from '../../../store/actions/frontApp.Action';
import { setPagination } from '../../../store/actions/Pagination.action';
import { LuRedoDot } from 'react-icons/lu';
import theme from '../../../theme';
import { getAllMasterDataForTable } from '../../../store/actions/masterData.action';
import { getCombinedProjects } from './helper';
import { TimeTrackerCell } from './TimeTrackerCell';

export const useBulkUpdate = () => {
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
    const dispatch = useDispatch();

    const handleBulkUpdate = (type: string, status: string) => {
        const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);

        if (selectedIds.length === 0) return;

        const apiPayload = {
            comment_ids: selectedIds,
            [type]: status
        };

        dispatch(updateBulkDTPM(apiPayload, () => {
            setRowSelection({});
        }));
    };

    return { rowSelection, setRowSelection, handleBulkUpdate };
};

const FrontAppListing: React.FC = () => {

    const { frontAppData, loader, updatingId } = useSelector((state: RootState) => state?.frontApp);
    const { paginations } = useSelector((state: RootState) => state.pagination);
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const location = useLocation();
    const [colorMode] = useColorMode();
    const [searchParams, setSearchParams] = useSearchParams();
    const { rowSelection, setRowSelection, handleBulkUpdate } = useBulkUpdate();
    const limit = parseInt(searchParams.get('limit') || "15");
    const currentPage = parseInt(searchParams.get('currentPage') || "1");
    useEffect(() => {
        const parsedQuery = queryString.parse(location.search);
        if (parsedQuery.currentPage !== String(currentPage) || parsedQuery.limit !== String(limit)) {
            setSearchParams({ ...parsedQuery, currentPage: String(currentPage), limit: String(limit) }, { replace: true });
        }
    }, [currentPage, limit, location.search, setSearchParams]);

    const { dataForTable } = useSelector((state: RootState) => state.masterData);
    const combinedProjects = useMemo(() => getCombinedProjects(dataForTable), [dataForTable]);

    useEffect(() => {
        dispatch(setPagination({ limit, currentPage }));
    }, [limit, currentPage]);

    useEffect(() => {
        dispatch(getAllMasterDataForTable());
    }, [dispatch]);

    useEffect(() => {
        const parsedQuery = queryString.parse(location.search);
        if (!parsedQuery.currentPage || !parsedQuery.limit) return; // Wait for URL to sync

        const apiEndPoint = getApiEndPoint({ limit, currentPage }, location.search, columns?.map((val: any) => ({ accessorKey: val.accessorKey, filterType: val.filterType })) || [])
        dispatch(getAllFrontapp(apiEndPoint));
    }, [location.search, limit, currentPage, dispatch]);
    const { data: eventSource } = useQuery({
        queryKey: ['frontAppSSE2'],
        queryFn: () => {
            const eventSource = new EventSource(`${baseUrl}sse/frontApp`);

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    dispatch(UpdateFrontAppRow(data))
                    if (data?.type === "Insert") {
                        toast((t) => (
                            <span>
                                New Message
                                <Text sx={{ margin: "0 10px", background: `${theme?.colors?.lightbackground}`, padding: "5px 8px", cursor: "pointer", borderRadius: "8px" }} onClick={() => {
                                    if (searchParams?.size > 2) {
                                        const parsedQuery = queryString.parse(location.search);

                                        setSearchParams({
                                            ...parsedQuery,
                                            currentPage: "1",
                                        });
                                    } else {
                                        setSearchParams({
                                            currentPage: "1",
                                            limit: limit.toString(),
                                        });
                                    }
                                    toast.remove();
                                }}>
                                    View
                                </Text>
                            </span>
                        ), { position: "top-center", duration: Infinity });
                    }
                } catch (error) {
                    console.error('Error parsing SSE data:', error);
                }
            };

            eventSource.onerror = (error) => {
                console.error('SSE Error:', error);
                eventSource.close();
            };

            return eventSource;
        },
        refetchInterval: 60000
    })
    useEffect(() => {
        return () => {
            eventSource?.close();
        };
    }, [])

    const CommentCell: React.FC<any> = ({ row }) => {
        const [expanded, setExpanded] = useState(false);
        const comment = row?.original?.comment;
        const truncatedComment = comment?.length > 15 ? comment.slice(0, 15) + '' : comment;

        const toggleExpanded = () => {
            setExpanded(!expanded);
        };

        const handleClick = () => {
            if (comment?.length > 15) {
                toggleExpanded();
            }
        };

        return (
            <div>
                <p onClick={handleClick} style={{ cursor: comment?.length > 15 ? 'pointer' : 'default' }}>

                    {expanded || comment?.length <= 15 ? comment : truncatedComment}
                    {comment?.length > 15 && !expanded && <span style={{ color: '#007FFF', marginLeft: '2px' }}>...</span>}
                </p>
            </div>
        );
    };

    const DToptions = [
        {
            value: "NotCompleted",
            label: "NotCompleted"
        },
        {
            value: "Completed",
            label: "Completed"
        }
    ]
    const PMoptions = [
        {
            value: "NotCompleted",
            label: "NotCompleted"
        },
        {
            value: "Completed",
            label: "Completed"
        }
    ]

    const columns = useMemo<ColumnDef<any>[]>(() => [
        {
            id: 'select',
            header: ({ table }) => (
                <input
                    type="checkbox"
                    checked={table.getIsAllPageRowsSelected()}
                    onChange={table.getToggleAllPageRowsSelectedHandler()}
                    className="form-check-input"
                    style={{ cursor: 'pointer', color: '#216ba5', accentColor: '#216ba5', borderColor: colorMode === 'light' ? 'transparent' : '#216ba5', width: '16px', height: '16px' }}
                />
            ),
            cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    className="form-check-input"
                    style={{ cursor: 'pointer', color: '#216ba5', accentColor: '#216ba5', borderColor: colorMode === 'light' ? 'transparent' : '#216ba5', width: '16px', height: '16px' }}
                />
            ),
            enableColumnFilter: false,
        },
        {
            header: 'No',
            enableColumnFilter: false,
            accessorKey: "no",
            cell: ({ row }) => {
                return (
                    <Text variant='Poppins14Normal24lh' sx={{ color: colorMode === 'light' ? '#4a4a4a' : '#f7efe5' }}>{((currentPage - 1) * limit) + row?.index + 1}</Text>
                )
            },
        },
        {
            accessorKey: "projectId",
            header: 'project name',
            cell: ({ row }) => {
                return (
                    <ProjectNameSelect originalRow={row?.original} colorMode={colorMode} />
                )
            },
            filterType: "dropdown",
            options: combinedProjects.map((val: { _id: string, name: string }) => ({ value: val._id, label: val.name }))

        },
        {
            accessorKey: "manager",
            header: 'Task Manager',
            cell: ({ row }) => {
                return (
                    <ManagerSelect originalRow={row?.original} colorMode={colorMode} />
                )
            },
            filterType: "dropdown",
            options: dataForTable?.data?.allTaskManagers?.map((val: { _id: string, FirstName: string, LastName: string }) => ({ value: val._id, label: `${val.FirstName || ''} ${val.LastName || ''}`.trim() }))

        },

        {
            header: 'Tab Name',
            accessorKey: "tabname",
            cell: ({ row }) => {
                return (
                    <Text>
                        {row?.original?.tabname}
                    </Text>
                );
            },
            filterType: "textbox",
        },
        {
            header: 'Date Received',
            accessorKey: "createdAt",
            cell: ({ row }) => {
                return (
                    <div className='d-flex justify-content-center align-items-center text-center'><img src={calender} alt="calender" style={{ marginRight: '5px' }} /> {row?.original?.createdAt?.split('T')[0] || ''}</div>
                )
            },
            filterType: 'date',
        },
        {
            header: "Due Date",
            accessorKey: "dueDate",
            cell: ({ row }) => {
                const dueDate = row?.original?.dueDate;
                if (!dueDate) return <div className='text-center'><Text variant='Poppins14Normal24lh' sx={{ color: colorMode === 'light' ? '#a0a0a0' : '#7a8599' }}>—</Text></div>;

                const due = new Date(dueDate.split('T')[0]);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isOverdue = due < today;

                const textColor = isOverdue
                    ? (colorMode === 'light' ? '#bf434ff4' : '#ff9999')
                    : (colorMode === 'light' ? '#0c7d27ff' : '#69f0ae');

                return (
                    <div className='d-flex justify-content-center align-items-center text-center'>
                        <img src={calender} alt="calender" style={{ marginRight: '3px' }} />
                        <span style={{ color: textColor, fontWeight: isOverdue ? 'bold' : 'normal' }}>
                            {dueDate?.split('T')[0] || ''}
                        </span>
                    </div>
                );
            },
            filterType: "date",
        },
        {
            header: 'Comment',
            accessorKey: "comment",
            cell: CommentCell,
            filterType: 'textbox',
        },
        {
            header: `Time Tracker`,
            accessorKey: "timer",
            cell: ({ row }) => {
                return (
                    <TimeTrackerCell item={row?.original} colorMode={colorMode} />
                );
            },
            enableColumnFilter: false,
        },
        {
            header: 'User Name',
            accessorKey: "username",
            cell: ({ row }) => {
                return (
                    <Text variant='Poppins14Normal24lh' sx={{ color: colorMode === 'light' ? '#4a4a4a' : '#f7efe5' }}>{row?.original?.username}</Text>
                );
            },
            filterType: 'textbox',
        },
        {
            header: 'Conversation',
            accessorKey: "conversation",
            cell: ({ row }) => {
                return (
                    <a href={row?.original?.cmt_conversation} target='_blank' rel='' className='text-decoration-none'><LuRedoDot className='me-1' />Link</a>
                )
            },
            filterType: "textbox",
        },
        {
            header: 'Comment Box',
            accessorKey: 'client_comment',

            cell: ({ row }) => {
                return (
                    <CommentBox original={row?.original} colorMode={colorMode} />
                );
            },
            filterType: "textbox",
        },
        {
            header: 'DT',
            accessorKey: "DT",
            cell: ({ row }) => {
                return (
                    <CheckboxInput
                        originalRow={row?.original}
                        checkboxType="DT" />
                )
            },
            filterType: "dropdown",
            options: DToptions
        },
        {
            header: 'PM',
            accessorKey: "PM",
            cell: ({ row }) => {
                return (
                    <CheckboxInput
                        originalRow={row?.original}
                        checkboxType="PM" />
                )
            },
            filterType: "dropdown",
            options: PMoptions
        },
    ], [frontAppData?.data?.length, currentPage, limit, dataForTable, colorMode]);

    const filteredData = useMemo(() => {
        return (frontAppData?.data || [])?.filter((item: FrontAppList) => (item?.PM || "")?.toLowerCase()?.trim() !== "completed");
    }, [frontAppData?.data]);
    return (
        <>
            <div className='main-content'>
                <div className="d-flex justify-content-between align-items-center">
                    <TabelHeadingTxt Title='Front App Pending Items' Count={frontAppData?.count} IsExportButtonShow={false} src={Export} BtnTxt='Export' url='/front-app/pending-items' ml={'80px'} />
                    {Object.keys(rowSelection).length > 0 && (
                        <div className="d-flex gap-2 pe-3">
                            {(() => {
                                const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
                                const selectedData = (frontAppData?.data || []).filter((item: any) => selectedIds.includes(item?.comment_id));
                                const isAnyDTCompleted = selectedData.some((item: any) => item?.DT === "Completed");
                                const isAnyPMCompleted = selectedData.some((item: any) => item?.PM === "Completed");

                                return (
                                    <>
                                        <Button
                                            variant="SaveButton"
                                            onClick={() => {
                                                const hasRunningTimer = selectedData.some((item: any) => item?.timer?.isRunning);
                                                if (hasRunningTimer) {
                                                    toast.error("Please pause the timer for the selected rows");
                                                    return;
                                                }
                                                handleBulkUpdate('DT', 'Completed');
                                            }}
                                            disabled={isAnyDTCompleted}
                                            sx={{ opacity: isAnyDTCompleted ? 0.6 : 1, cursor: isAnyDTCompleted ? 'not-allowed' : 'pointer' }}
                                        >
                                            Complete DT
                                        </Button>
                                        <Button
                                            variant="SaveButton"
                                            onClick={() => {
                                                const hasRunningTimer = selectedData.some((item: any) => item?.timer?.isRunning);
                                                if (hasRunningTimer) {
                                                    toast.error("Please pause the timer for the selected rows");
                                                    return;
                                                }
                                                handleBulkUpdate('PM', 'Completed');
                                            }}
                                            disabled={isAnyPMCompleted}
                                            sx={{ opacity: isAnyPMCompleted ? 0.6 : 1, cursor: isAnyPMCompleted ? 'not-allowed' : 'pointer' }}
                                        >
                                            Complete PM
                                        </Button>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </div>
                <Divider sx={{ color: '#D9D7DD' }} opacity={2} my={[3, 3, 4]} />
                <FrontAppTable
                    isPending={true}
                    loader={loader}
                    updatingId={updatingId}
                    highlightCompleted={true}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection={true}
                    columns={columns}
                    data={filteredData}
                    TotalLength={frontAppData?.count || 15}
                    perPageCount={paginations?.limit}
                    currentPage={paginations?.currentPage}
                    onPerPageChange={(itemsPerPage: string) => {
                        const parsedQuery = queryString?.parse(location.search);
                        setSearchParams({ ...parsedQuery, limit: itemsPerPage.toString() });
                    }}
                    onPageChange={(value) => {
                        const parsedQuery = queryString.parse(location.search);
                        setSearchParams({ ...parsedQuery, currentPage: value.toString() });
                    }} />
            </div>
        </>
    )
};

export default FrontAppListing;
